/* Shared behaviour.
   Motion: the clone's measured easing curves (cubic-bezier(.165,.84,.44,1)) at
   Tiger's durations — motion.micro/element/surface in foundation/tiger.tokens.json.

   This is the static site's js/main.js carried over as-is. Six Shopify-shaped
   changes, each marked SHOPIFY below, are the whole diff — commerce behaviour
   lives in tiger-shop.js rather than being threaded through here, so the two
   files can still be diffed against static-site/js/main.js. */
(function () {
  'use strict';

  /* SHOPIFY 1/6: asset paths are hashed by the CDN, so they arrive from the
     layout rather than being written as relative URLs. */
  var CFG = window.Tiger || {};
  var ASSETS = CFG.assets || {};

  /* ---- loading overlay ----
     The clone's route-change scrim, mechanism kept: shown on link click with a
     120ms delay (an instant local navigation shouldn't flash it), dismissed on
     pageshow/pagehide. The artwork is the static Tiger lockup, painted by the
     stylesheet's mask — nothing to pick per navigation any more. */
  var loader = document.getElementById('page-loader');
  if (loader) {
    var showTimer = null;
    var showLoader = function () {
      loader.hidden = false;
      loader.setAttribute('aria-hidden', 'false');
    };
    var hideLoader = function () {
      clearTimeout(showTimer);
      loader.hidden = true;
      loader.setAttribute('aria-hidden', 'true');
    };

    document.addEventListener('click', function (e) {
      // a click that will not navigate this page must never arm the scrim:
      // modifier clicks open elsewhere, and a prevented click (e.g. the swipe
      // guard's) goes nowhere — either would strand the overlay on screen
      if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.charAt(0) === '#' || a.target === '_blank') return;
      if (/^(https?:|mailto:|tel:)/i.test(href)) return;
      /* SHOPIFY 2/6: a colour swatch is a real link so it still works without
         JS, but when JS does answer it the swap happens in place — arming the
         scrim there would strand it on screen over a page that never left. */
      if (a.hasAttribute('data-no-loader')) return;
      // brief delay: an instant local navigation shouldn't flash the scrim
      showTimer = setTimeout(showLoader, 120);
    });

    // returning via the back/forward cache must not leave the scrim up
    window.addEventListener('pageshow', hideLoader);
    window.addEventListener('pagehide', hideLoader);
  }

  /* ---- first-load mark reveal (motion.markReveal) ----
     Plays once per session, on first load only; every later navigation uses
     the static scrim above (DECISIONS.md D-13). The emblem's 36 contours
     stroke in head-first, right to left, at a 30ms stagger; the filled lockup
     then crossfades in, so the final frame is site/assets/tiger-lockup.svg
     verbatim — the vector is revealed, never redrawn. The wordmark is custom
     lettering and is never restated as an outline: its contours arrive only
     with the filled crossfade. Skipped under prefers-reduced-motion (the
     solid lockup already stands in the header and scrim) and when first
     contentful paint exceeds 1500ms; pointer-events stay off throughout —
     the reveal never delays input. */
  (function () {
    var KEY = 'tiger-mark-reveal-played';
    try { if (sessionStorage.getItem(KEY)) return; } catch (e) { return; }
    // consume the once-per-session attempt now, not after the FCP gate —
    // otherwise a slow first paint defers the reveal to a later navigation,
    // against D-13's "first load only"
    try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var STAGGER = 30;                              // motion.markReveal.staggerMs
    var TOTAL = 2400;                              // motion.mark
    var DRAW = 900;   // derived: stroke duration, fits the 2400ms envelope
    var FADE = 500;   // derived: crossfade share of the envelope
    var EASE = 'cubic-bezier(.45,.05,.25,1)';      // motion.easeMark
    var FCP_LIMIT = 1500;                          // motion.markReveal.neverGates

    var start = function (fcpMs) {
      if (fcpMs > FCP_LIMIT) return;
      fetch(ASSETS.lockup || 'tiger-lockup.svg')   /* SHOPIFY 1/6 */
        .then(function (r) { return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          var src = doc.querySelector('svg');
          var srcPath = src && src.querySelector('path');
          var d = srcPath && srcPath.getAttribute('d');
          if (!d) return;

          var NS = 'http://www.w3.org/2000/svg';
          var overlay = document.createElement('div');
          overlay.className = 'notification-overlay is-reveal';
          overlay.setAttribute('aria-hidden', 'true');
          var slot = document.createElement('div');
          slot.className = 'loader';
          var box = document.createElement('div');
          box.className = 'image-loader';
          var svg = document.createElementNS(NS, 'svg');
          svg.setAttribute('viewBox', src.getAttribute('viewBox'));

          // the filled artwork, verbatim — this is the final frame
          var fill = document.createElementNS(NS, 'path');
          fill.setAttribute('d', d);
          fill.setAttribute('fill', 'currentColor');
          fill.setAttribute('class', 'reveal-fill');

          // the same outline data, split at its M commands so each contour
          // can draw independently; nothing is re-plotted
          var contours = d.match(/M[^M]+/g) || [];
          var strokes = document.createElementNS(NS, 'g');
          contours.forEach(function (part) {
            var p = document.createElementNS(NS, 'path');
            p.setAttribute('d', part);
            p.setAttribute('class', 'reveal-contour');
            p.setAttribute('pathLength', '1');
            p.style.strokeDasharray = '1';
            p.style.strokeDashoffset = '1';
            strokes.appendChild(p);
          });

          svg.appendChild(strokes);
          svg.appendChild(fill);
          box.appendChild(svg);
          slot.appendChild(box);
          overlay.appendChild(slot);
          document.body.appendChild(overlay);

          // emblem contours only (the engraving); the lockup splits cleanly
          // at y≈520 between emblem (above) and wordmark (below)
          var emblem = [];
          Array.prototype.forEach.call(strokes.children, function (p) {
            var b = p.getBBox();
            if (b.y + b.height / 2 < 520) {
              emblem.push({ p: p, x: b.x + b.width / 2 });
            } else {
              p.style.display = 'none';
            }
          });
          emblem.sort(function (a, b) { return b.x - a.x; });

          requestAnimationFrame(function () { requestAnimationFrame(function () {
            emblem.forEach(function (c, i) {
              c.p.style.transition =
                'stroke-dashoffset ' + DRAW + 'ms ' + EASE + ' ' + (i * STAGGER) + 'ms';
              c.p.style.strokeDashoffset = '0';
            });
            setTimeout(function () {
              fill.style.transition = 'opacity ' + FADE + 'ms ' + EASE;
              fill.style.opacity = '1';
              strokes.style.transition = 'opacity ' + FADE + 'ms ' + EASE;
              strokes.style.opacity = '0';
            }, TOTAL - FADE);
            setTimeout(function () {
              overlay.classList.add('is-done');
              setTimeout(function () { overlay.remove(); }, 600);
            }, TOTAL);
          }); });
        })
        .catch(function () { /* no reveal; the static mark stands */ });
    };

    // FCP gate: observe paint timing; if it never arrives in time, skip quietly
    try {
      var seen = false;
      var po = new PerformanceObserver(function (list) {
        var e = list.getEntriesByName('first-contentful-paint')[0];
        if (e && !seen) { seen = true; po.disconnect(); start(e.startTime); }
      });
      po.observe({ type: 'paint', buffered: true });
      setTimeout(function () { if (!seen) { seen = true; po.disconnect(); } }, FCP_LIMIT + 100);
    } catch (e) { /* older engine: no reveal, the static mark stands */ }
  })();

  var header = document.querySelector('.header-container');
  var nav = document.querySelector('.header-nav');
  var items = Array.prototype.slice.call(document.querySelectorAll('.menu-bar-list > li'));
  var desktop = window.matchMedia('(min-width: 1024px)');

  /* ---- mega menu: hover on desktop, tap-to-expand on mobile ---- */
  var closeTimer = null;
  var openTimer = null;

  function closeAll(except) {
    items.forEach(function (li) {
      if (li !== except) {
        li.classList.remove('is-open');
        var b = li.querySelector('.category-link');
        if (b) b.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /* Each panel's editorial image is fetched the first time that panel is
     reached, not on page load. loading="lazy" does not help here: a closed
     panel is only hidden, not out of the viewport, so the browser fetches it
     anyway — four tiles on every page of the site, for a menu most visits never
     open. Skipped entirely below the desktop breakpoint, where the drawer does
     not show the image at all. */
  function mountFeature(li) {
    if (!desktop.matches) return;
    var img = li.querySelector('.universe-feature img[data-src]');
    if (!img) return;
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
  }

  items.forEach(function (li) {
    var btn = li.querySelector('.category-link');
    if (!btn) return;

    li.addEventListener('mouseenter', function () {
      if (!desktop.matches) return;
      /* fetch on intent, before the open delay, so the panel is complete by the
         time it appears */
      mountFeature(li);
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      /* 120ms of intent before opening from rest, so a pointer crossing the bar
         on its way somewhere else does not flash three panels open. Moving
         between universes while one is already open swaps immediately — the
         delay guards the first open, not the traverse. */
      var anyOpen = items.some(function (x) { return x.classList.contains('is-open'); });
      openTimer = setTimeout(function () {
        closeAll(li);
        li.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }, anyOpen ? 0 : 120);
    });

    li.addEventListener('mouseleave', function () {
      if (!desktop.matches) return;
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () {
        li.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
      }, 200);
    });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      clearTimeout(openTimer);
      mountFeature(li);
      var open = li.classList.contains('is-open');
      closeAll(li);
      li.classList.toggle('is-open', !open);
      btn.setAttribute('aria-expanded', String(!open));
    });

    btn.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeAll(null); btn.blur(); }
    });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });

  /* ---- mobile drawer ---- */
  var toggle = document.querySelector('.menu-toggle');
  if (toggle && nav) {
    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      /* Where the bar actually ends, which is not a constant: the study banner
         sits above the header and scrolls away, so the bar is at 37px at rest
         and 0 once the page has moved. The drawer hangs off this. Measured on
         open only — body scrolling is locked while it is open, so the bar
         cannot move underneath it. */
      if (open) {
        var bar = document.querySelector('.header-main');
        if (bar) {
          document.documentElement.style.setProperty(
            '--drawer-top', Math.round(bar.getBoundingClientRect().bottom) + 'px');
        }
      }
    });
  }

  /* ---- language -------------------------------------------------------
     What this is honest about: the toggle turns the page over to Arabic —
     direction, alignment, drawer side, and the wording of the chrome. It does
     not translate the catalogue. Product names, prices and the long-form
     pages stay in English, because 138 product titles and fourteen pages of
     copy are a translation job, not a switch.

     Every Arabic string lives in this one object, as on the homepage, so a
     native speaker can correct the wording in one pass. These are drafts.

     The layout can take it: the stylesheet uses 27 logical properties and not
     one physical margin-left or padding-right, and already carries :dir(rtl)
     rules for the drawer and the cart tray. Turning the page over was
     designed for; it had simply never been switched on.
     --------------------------------------------------------------------- */
  var AR = {
    'Menu': 'القائمة',
    'Search': 'بحث',
    'Browse': 'تصفّح',
    'Account': 'الحساب',
    'Cart': 'السلة',
    'Your cart': 'سلّتك',
    'Your cart is empty.': 'سلّتك فارغة.',
    'Continue shopping': 'متابعة التسوّق',
    'Men': 'رجال',
    'Women': 'نساء',
    'Comfort & Footbed': 'الراحة والنعل الطبي',
    'Comfort & footbed': 'الراحة والنعل الطبي',
    'The Atelier': 'الورشة',
    'Arabic & toe-post sandals': 'صنادل عربية وإصبعية',
    'Dress slides': 'أحذية رسمية',
    'Sabo': 'صابو',
    'Comfort slides': 'حذاء مريح',
    'Sport sandals': 'صنادل رياضية',
    'Sneakers': 'سنيكرز',
    'Wedges': 'كعب وتد',
    'Genuine leather': 'جلد طبيعي',
    'Footbed': 'نعل طبي',
    "Women's wedges": 'كعب وتد نسائي',
    "View all men's": 'عرض كل الرجالي',
    "View all women's": 'عرض كل النسائي',
    'View all comfort': 'عرض كل المريح',
    'About Tiger': 'عن تايجر',
    'Since 1978': 'منذ ١٩٧٨',
    'How a pair is finished': 'كيف يُصنع الحذاء',
    'Sustainability': 'الاستدامة',
    'Wholesale and export': 'الجملة والتصدير',
    /* footer */
    'Services': 'الخدمات',
    'Contact us': 'اتصل بنا',
    'FAQ': 'الأسئلة الشائعة',
    'Care': 'العناية',
    'Orders': 'الطلبات',
    'Payment': 'الدفع',
    'Shipping': 'الشحن',
    'Returns and exchanges': 'الإرجاع والاستبدال',
    'Subscribe to the newsletter': 'اشترك في النشرة البريدية',
    'Ship to:': 'الشحن إلى:',
    'Jordan': 'الأردن',
    'Change': 'تغيير',
    'Here to help': 'نحن هنا للمساعدة',
    'Call us': 'اتصل بنا هاتفياً',
    'Email us': 'راسلنا',
    'Visit us': 'زرنا',
    'Amman showrooms': 'معارض عمّان',
    'Terms and conditions': 'الشروط والأحكام',
    'Privacy': 'الخصوصية'
    /* Zaghal Industrial Co., Facebook, Instagram and YouTube are proper nouns
       and stay as they are. */
  };

  /* Only the chrome is translated, so only the chrome is walked — running this
     over <main> would rewrite product titles that have no Arabic to go to. */
  var LANG_SCOPES = ['.header-container', '.site-footer'];

  function swapChrome(toArabic) {
    LANG_SCOPES.forEach(function (sel) {
      var root = document.querySelector(sel);
      if (!root) return;
      Array.prototype.forEach.call(root.querySelectorAll('*'), function (el) {
        if (el.children.length) return;                 /* leaf text only */
        if (toArabic) {
          var en = (el.textContent || '').trim();
          if (!en || !AR[en]) return;
          el.setAttribute('data-en', en);               /* so it can come back */
          el.textContent = AR[en];
          el.setAttribute('lang', 'ar');
        } else if (el.hasAttribute('data-en')) {
          el.textContent = el.getAttribute('data-en');
          el.removeAttribute('data-en');
          el.removeAttribute('lang');
        }
      });
    });
  }

  function applyLang(lang) {
    var ar = lang === 'ar';
    var d = document.documentElement;
    d.setAttribute('lang', ar ? 'ar' : 'en-us');
    d.setAttribute('dir', ar ? 'rtl' : 'ltr');
    d.setAttribute('data-lang', ar ? 'ar' : 'en');
    swapChrome(ar);
    /* the button names the language you would get, not the one you are in */
    var label = document.querySelector('.lang-toggle-label');
    if (label) {
      label.textContent = ar ? 'EN' : 'عربي';
      label.setAttribute('lang', ar ? 'en' : 'ar');
    }
    var btn = document.querySelector('.lang-toggle');
    if (btn) btn.setAttribute('aria-label', ar ? 'Switch to English' : 'التبديل إلى العربية');
    try { localStorage.setItem('tiger-lang', ar ? 'ar' : 'en'); } catch (e) {}
  }

  var langBtn = document.querySelector('.lang-toggle');
  if (langBtn) {
    langBtn.addEventListener('click', function () {
      applyLang(document.documentElement.getAttribute('data-lang') === 'ar' ? 'en' : 'ar');
    });
  }
  /* the inline bootstrap in the header already set direction if Arabic was
     stored; this finishes the job by swapping the words */
  var stored;
  try { stored = localStorage.getItem('tiger-lang'); } catch (e) {}
  if (stored === 'ar') applyLang('ar');

  /* SHOPIFY 6/6: tiger-shop.js re-renders the cart tray and the search panel's
     Browse list from server HTML, which arrives in English. Republishing the
     chrome swap lets it re-apply the reader's language to what it just
     replaced, without owning any of the translation machinery. */
  CFG.relocalize = function () {
    if (document.documentElement.getAttribute('data-lang') === 'ar') swapChrome(true);
  };

  /* Ported from the static site by hand, not by sync-theme.mjs: the stylesheet
     is a verbatim copy but this file is not, so each block is carried across
     deliberately. The banner needs sections/hero.liquid to emit one .hero-shot
     per slide before it has anything to turn. */

  /* ---- banner slideshow ----
     Auto-advance is a convenience, never the only way through: the dots drive
     it directly, hovering or focusing the banner holds it, and a reader who has
     asked for reduced motion gets a still first plate and the dots. */
  (function () {
    var banner = document.querySelector('[data-slideshow]');
    if (!banner) return;
    var shots = banner.querySelectorAll('.hero-shot');
    var caps = banner.querySelectorAll('.hero-caption');
    var dots = banner.querySelectorAll('.hero-dot');
    if (shots.length < 2) return;

    var HOLD = 6000;
    var i = 0, timer = null;
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function show(n) {
      i = (n + shots.length) % shots.length;
      for (var k = 0; k < shots.length; k++) {
        var on = k === i;
        shots[k].classList.toggle('is-active', on);
        if (caps[k]) {
          caps[k].classList.toggle('is-active', on);
          /* keep the hidden captions out of the reading order and off the tab
             ring, not merely invisible */
          if (on) { caps[k].removeAttribute('aria-hidden'); } else { caps[k].setAttribute('aria-hidden', 'true'); }
          var cta = caps[k].querySelector('.cta');
          if (cta) { if (on) { cta.removeAttribute('tabindex'); } else { cta.setAttribute('tabindex', '-1'); } }
        }
        if (dots[k]) {
          dots[k].classList.toggle('is-active', on);
          if (on) { dots[k].setAttribute('aria-current', 'true'); } else { dots[k].removeAttribute('aria-current'); }
        }
      }
      /* the blurred mount is built from the plate behind it, so it turns too */
      var img = shots[i].querySelector('img');
      var src = img && (img.currentSrc || img.src);
      if (src) banner.style.setProperty('--plate-src', 'url("' + src.replace(/"/g, '\\"') + '")');
    }

    /* The slides after the first are marked lazy so they do not compete with
       the opening plate for the first paint — but they are stacked at opacity 0
       and never intersect anything, so the browser is within its rights never
       to fetch them at all, and the banner then turns onto a black frame.
       Dropping the attribute once the page has loaded starts the fetch: the
       opening plate still gets the network to itself, and the rest are in place
       long before the first turn six seconds later. */
    function warmSlides() {
      Array.prototype.forEach.call(shots, function (s) {
        var im = s.querySelector('img');
        if (im && im.getAttribute('loading') === 'lazy') im.removeAttribute('loading');
      });
    }
    if (document.readyState === 'complete') warmSlides();
    else window.addEventListener('load', warmSlides, { once: true });

    function play() { if (!still && !timer) timer = setInterval(function () { show(i + 1); }, HOLD); }
    function pause() { if (timer) { clearInterval(timer); timer = null; } }

    Array.prototype.forEach.call(dots, function (d, n) {
      d.addEventListener('click', function () { pause(); show(n); play(); });
    });
    banner.addEventListener('pointerenter', pause);
    banner.addEventListener('pointerleave', play);
    banner.addEventListener('focusin', pause);
    banner.addEventListener('focusout', play);
    /* a tab in the background should not burn through the whole banner */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { pause(); } else { play(); }
    });
    play();
  })();

  /* ---- colour swatches on a listing: dress the card, don't leave the page ----
     A swatch under a card used to be a link to that colourway's own page, so
     glancing at a shoe in brown meant a page load, a scroll position lost, and
     the back button to get home. It now changes the card where it stands.

     The art is copied from the colourway's own card elsewhere on the same page
     rather than guessed from a filename: that brings the real second shot and
     its dots with it, which a derived URL could not. Where the colourway has no
     card on this page — a filtered listing, mainly — the swatch's own 44px
     thumbnail is the same photograph, so the full-size version of it stands in.

     Only listing swatches are caught here. On a product page changing colour is
     a change of product, and that still navigates. */
  (function () {
    if (!document.querySelector('.product-item-wrap')) return;

    /* A card and a swatch are paired by variant id — stamped by
       snippets/product-card.liquid and snippets/swatches.liquid. This was the
       static build's product__{slug}.html filename, which no Shopify URL can
       ever match: the slug came back null, the guard below handed the click
       back to the browser, and every swatch on a listing navigated away.

       Each card's own art is snapshotted here, at load, rather than read live
       off the card when a swatch is clicked. Read live, dressing card A in B's
       colour would leave A holding B's photograph, and a later click asking A
       for its own colour back would be answered with B's. */
    var byVariant = {};
    Array.prototype.forEach.call(document.querySelectorAll('.product-item-wrap[data-variant-id]'), function (w) {
      var id = w.getAttribute('data-variant-id');
      if (!id || byVariant[id]) return;
      var m = w.querySelector('.product-media');
      var meta = w.querySelector('.product-item-meta');
      var buy = w.querySelector('.quick-buy');
      byVariant[id] = {
        media: m ? m.innerHTML : null,
        shots: m ? (m.getAttribute('data-shots') || '1') : '1',
        meta: meta ? meta.innerHTML : null,
        colour: w.getAttribute('data-color') || '',
        buy: buy ? buy.cloneNode(true) : null
      };
    });

    /* The quick-buy is replaced whole, not relabelled. It carries
       data-quick-add — the variant id tiger-shop.js puts in the bag — so a
       relabelled button would have added the colour the shopper had just
       moved on from. Where the colour has no card on this page its size run is
       unknown, so the control degrades to a link at that colour, which is what
       product-card.liquid itself does when a colourway is more than one
       variant: never a button that adds an arbitrary size. */
    var dressQuickBuy = function (wrap, snap, sw) {
      var mine = wrap.querySelector('.quick-buy');
      var swatches = wrap.querySelector('.swatches');

      if (snap) {
        if (snap.buy) {
          var clone = snap.buy.cloneNode(true);   /* the snapshot itself is never handed out */
          if (mine) wrap.replaceChild(clone, mine);
          else wrap.insertBefore(clone, swatches || null);
        } else if (mine) {
          wrap.removeChild(mine);                 /* that colourway is sold out */
        }
        return;
      }

      if (!mine) return;
      var a = document.createElement('a');
      a.className = mine.className;
      a.setAttribute('href', sw.getAttribute('href'));
      a.innerHTML = mine.innerHTML;
      var title = wrap.querySelector('.product-title');
      if (title) {
        a.setAttribute('aria-label',
          'Add ' + title.textContent.trim() + ', ' + (sw.getAttribute('data-colour') || sw.getAttribute('title') || '') + ', to cart');
      }
      wrap.replaceChild(a, mine);
    };

    /* Capture, not bubble, and that is load-bearing. The loading scrim listens
       for link clicks on document and arms itself unless the click has already
       been prevented — but it is registered further up this file, so in the
       bubble phase it runs first, sees a live click, and arms. This handler
       then cancels the navigation, nothing ever loads, no pageshow fires, and
       the Tiger scrim sits on the screen for good. Capturing puts the
       preventDefault in before the scrim looks, which is precisely the case its
       own guard was written for. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var sw = e.target.closest('.swatches .swatch');
      if (!sw) return;
      /* a modifier or middle click means "open this elsewhere" — leave it be */
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (sw.classList.contains('is-active')) {
        e.preventDefault();                  /* re-picking the current colour does nothing */
        return;
      }
      var wrap = sw.closest('.product-item-wrap');
      var link = wrap && wrap.querySelector('a.product-item');
      var media = wrap && wrap.querySelector('.product-media');
      var href = sw.getAttribute('href');
      var variantId = sw.getAttribute('data-variant-id');
      if (!wrap || !link || !media || !href || !variantId) return;   /* anything unexpected: let the link work */

      e.preventDefault();
      var snap = byVariant[variantId];
      var colour = sw.getAttribute('data-colour') || sw.getAttribute('title') || '';

      if (snap && snap.media) {
        var meta = wrap.querySelector('.product-item-meta');
        media.innerHTML = snap.media;
        media.setAttribute('data-shots', snap.shots);
        if (snap.meta && meta) meta.innerHTML = snap.meta;
        wrap.setAttribute('data-color', snap.colour);
      } else {
        /* No card for this colour on the page — a filtered listing, or a colour
           past the swatch limit. The swatch's own thumbnail is the same
           photograph, so the full-size version of it stands in. */
        var thumb = sw.querySelector('img');
        var full = thumb && (thumb.getAttribute('src') || '').split('?')[0];
        if (full) {
          media.setAttribute('data-shots', '1');
          media.innerHTML =
            '<div class="pm-hover"><img class="pm-img pm-primary" src="' + full + '" alt="' + colour + '" decoding="async"></div>' +
            '<ul class="pm-swipe"><li class="pm-slide"><img class="pm-fit" src="' + full + '" alt="' + colour + '" decoding="async"></li></ul>';
        }
        wrap.setAttribute('data-color', colour);
      }

      /* the card now points at the colour it is showing */
      link.setAttribute('href', href);
      Array.prototype.forEach.call(wrap.querySelectorAll('.swatches .swatch'), function (s) {
        s.classList.toggle('is-active', s === sw);
      });
      dressQuickBuy(wrap, snap, sw);
      bindCardGallery(media);
    }, true);
  })();

  /* ---- colour on a product page: a change of dress, not a change of place ----
     Each swatch here is an ordinary link, so every colour tried pushed its own
     history entry: look at four colours and Back walks you through all four
     before it lets you out. The reader did not travel four times — they stood
     in one place and turned the shoe over — and what they mean by Back is the
     listing they arrived from.

     location.replace navigates for real but writes over the current entry
     instead of stacking on it, so history stays [listing, this product] however
     many colours are tried, and one Back lands where they came in. This also
     fixes the page's own back pill, which calls history.back().

     Deliberately in the bubble phase, unlike the listing swatches above: this
     one really does navigate, so the loading scrim should arm as it does for
     any other route change, and the incoming page's pageshow clears it. Related
     products keep pushing — those are somewhere else, not another colour. */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    if (!e.target.closest) return;
    var sw = e.target.closest('.pdp-swatches .pdp-swatch');
    if (!sw) return;
    var href = sw.getAttribute('href');
    if (!href || /^(https?:|mailto:|tel:|#)/i.test(href)) return;
    e.preventDefault();
    location.replace(href);
  });

  /* ---- mount the blurred plate backdrop ----
     The hero photograph differs per page, so the backdrop image has to as well.
     Reading it off the <img> and publishing it as a custom property keeps that
     per-page without touching any of the 151 HTML files. No extra download —
     the browser reuses the cached decode of the same URL. */
  Array.prototype.forEach.call(
    document.querySelectorAll('.block-hero'),
    function (el) {
      var img = el.querySelector('img');
      var src = img && (img.currentSrc || img.src);
      if (src) el.style.setProperty('--plate-src', 'url("' + src.replace(/"/g, '\\"') + '")');
    }
  );

  /* ---- adaptive header: the theme is sampled from the pixels behind the bar ----

     The cream/ink pair has a hard WCAG AA dead zone. Cream (#F2EFE8, L=0.8644)
     clears 4.5:1 only below background luminance 0.1532; ink (#1C1E23, L=0.0130)
     only above 0.2335. Between them BOTH fail — best achievable is 3.81:1 — and
     real frames land there: cat-genuine-leather.jpg measures 0.164. So picking a
     colour is not enough. This solves for the smallest scrim that carries the
     worst zone past 4.5:1, over both candidate themes, and takes the cheaper.

     It also samples three horizontal zones rather than one average, because a
     single mean hides split frames: cat-signature-sandals has a left third at
     0.149 (wants cream) and a right third at 0.445 (wants ink). */
  var OVERLAY_ON_TOUCH = false;   /* flip to true to overlay on phones too — see plan */

  var hero = document.querySelector('[data-hero]');
  var heroImg = hero && hero.querySelector('img');
  var hoverCapable = !window.matchMedia || window.matchMedia('(hover: hover)').matches;
  var overlayAllowed = OVERLAY_ON_TOUCH || hoverCapable;

  if (header && hero && heroImg && overlayAllowed) {
    /* WCAG AA is 4.5:1; solve to 5.0 for margin. The mount's contribution is
       modelled from the plate rather than measured off the composited frame —
       the blur is a cover-crop of the same photo, scaled 1.15 — so leave room
       for that approximation. Without the margin, cat-genuine-leather (the one
       frame sitting inside the dead zone) settles a ladder step short at 4.13:1. */
    var TARGET = 5.0;
    var L_CREAM = 0.8644, L_INK = 0.0130;
    var CHAR = [0x14, 0x17, 0x1D], GROUND = [0xF7, 0xF4, 0xEE];
    var MOUNT_BRIGHTNESS = 0.4;   /* must track the filter in .block-hero::before */
    var SCRIM_STEPS = [0, 0.08, 0.16, 0.24, 0.32, 0.40, 0.48];
    var ZONES = 3, ZONE_PX = 32, BANDS = 48;

    function toLinear(c) {
      c /= 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    function toSrgb(v) {
      v = v < 0 ? 0 : v > 1 ? 1 : v;
      return 255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055);
    }
    function lum(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }
    function ratio(a, b) {
      var hi = a > b ? a : b, lo = a > b ? b : a;
      return (hi + 0.05) / (lo + 0.05);
    }

    /* One canvas readback for the page lifetime. Linearising BEFORE averaging is
       mandatory: averaging sRGB bytes then linearising reports L=0.214 for a
       half-black/half-white band whose true luminance is 0.500. */
    var profile = null;
    function buildProfile(img) {
      try {
        var c = document.createElement('canvas');
        c.width = ZONES * ZONE_PX; c.height = BANDS;
        var ctx = c.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, c.width, c.height);
        var d = ctx.getImageData(0, 0, c.width, c.height).data;
        var out = new Float32Array(BANDS * ZONES * 3);
        for (var y = 0; y < BANDS; y++) {
          for (var z = 0; z < ZONES; z++) {
            var r = 0, g = 0, b = 0;
            for (var i = 0; i < ZONE_PX; i++) {
              var o = (y * c.width + z * ZONE_PX + i) * 4;
              r += toLinear(d[o]); g += toLinear(d[o + 1]); b += toLinear(d[o + 2]);
            }
            var k = (y * ZONES + z) * 3;
            out[k] = r / ZONE_PX; out[k + 1] = g / ZONE_PX; out[k + 2] = b / ZONE_PX;
          }
        }
        return out;
      } catch (e) {
        return null;   /* tainted canvas (file://) — caller keeps the safe state */
      }
    }

    /* getBoundingClientRect gives the element box, not the painted box; under
       object-fit: contain those differ. */
    function paintedBox(img) {
      var r = img.getBoundingClientRect();
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh || !r.width) return null;
      var cs = getComputedStyle(img);
      var padL = parseFloat(cs.paddingLeft) || 0, padR = parseFloat(cs.paddingRight) || 0;
      var padT = parseFloat(cs.paddingTop) || 0, padB = parseFloat(cs.paddingBottom) || 0;
      var cw = r.width - padL - padR, ch = r.height - padT - padB;
      var s = cs.objectFit === 'cover' ? Math.max(cw / nw, ch / nh) : Math.min(cw / nw, ch / nh);
      var pw = nw * s, ph = nh * s;
      var pos = cs.objectPosition.split(' ');
      var fx = (parseFloat(pos[0]) || 50) / 100;
      var fy = (parseFloat(pos[1] !== undefined ? pos[1] : pos[0]) || 50) / 100;
      return {
        left: r.left + padL + (cw - pw) * fx,
        top: r.top + padT + (ch - ph) * fy,
        width: pw, height: ph
      };
    }

    function ratioAfterScrim(linRgb, scrim, a, Ltext) {
      var r = toSrgb(linRgb[0]) * (1 - a) + scrim[0] * a;
      var g = toSrgb(linRgb[1]) * (1 - a) + scrim[1] * a;
      var b = toSrgb(linRgb[2]) * (1 - a) + scrim[2] * a;
      return ratio(lum(toLinear(r), toLinear(g), toLinear(b)), Ltext);
    }

    var CANDIDATES = [
      { name: 'on-dark',  scrim: CHAR,   Ltext: L_CREAM },
      { name: 'on-light', scrim: GROUND, Ltext: L_INK }
    ];

    /* Lowest alpha whose WORST zone clears AA; ties go to the higher contrast.
       Targeting the worst zone rather than the average is what catches split
       frames; targeting the worst individual cell would instead chase blown
       highlights and force a heavy scrim everywhere. */
    function solve(zones) {
      var best = null;
      for (var ci = 0; ci < CANDIDATES.length; ci++) {
        var cand = CANDIDATES[ci];
        for (var si = 0; si < SCRIM_STEPS.length; si++) {
          var a = SCRIM_STEPS[si], worst = Infinity;
          for (var z = 0; z < ZONES; z++) {
            var v = ratioAfterScrim(zones[z], cand.scrim, a, cand.Ltext);
            if (v < worst) worst = v;
          }
          if (worst >= TARGET) {
            if (!best || a < best.alpha || (a === best.alpha && worst > best.worst)) {
              best = { theme: cand.name, alpha: a, worst: worst };
            }
            break;
          }
        }
      }
      return best || { theme: 'on-light', alpha: 1, worst: 15.19 };
    }

    /* Zone colour = area-weighted mix, in linear light, of the plate (indexed
       out of the profile) and the mount (the same pixels darkened by the blur's
       brightness multiplier). Off the hero entirely, it is the page ground —
       which is how non-hero pages and the scrolled-past state resolve to ink
       through this same path instead of as special cases. */
    var GROUND_LIN = [toLinear(GROUND[0]), toLinear(GROUND[1]), toLinear(GROUND[2])];

    function zoneColour(zi, bandTop, bandBot, box, headerLeft, headerWidth) {
      var zw = headerWidth / ZONES;
      var zL = headerLeft + zi * zw, zR = zL + zw;
      var out = [0, 0, 0];
      if (!profile || !box) return GROUND_LIN.slice();

      var oL = Math.max(zL, box.left), oR = Math.min(zR, box.left + box.width);
      var oT = Math.max(bandTop, box.top), oB = Math.min(bandBot, box.top + box.height);
      var bandArea = (zR - zL) * (bandBot - bandTop);
      var imgArea = Math.max(0, oR - oL) * Math.max(0, oB - oT);
      if (bandArea <= 0) return GROUND_LIN.slice();

      var imgW = imgArea / bandArea;
      if (imgW <= 0) return GROUND_LIN.slice();

      /* average the profile bands the header band actually covers */
      var b0 = Math.floor(((oT - box.top) / box.height) * BANDS);
      var b1 = Math.ceil(((oB - box.top) / box.height) * BANDS);
      b0 = Math.max(0, Math.min(BANDS - 1, b0));
      b1 = Math.max(b0 + 1, Math.min(BANDS, b1));
      var r = 0, g = 0, b = 0, n = 0;
      for (var y = b0; y < b1; y++) {
        var k = (y * ZONES + zi) * 3;
        r += profile[k]; g += profile[k + 1]; b += profile[k + 2];
        n++;
      }
      r /= n; g /= n; b /= n;

      /* The rest of the strip is mount: the same photograph, blurred and
         darkened. Model it from the SAME bands scaled by the blur's brightness
         multiplier — using a different region here is what let the solver
         under-shoot on the one frame inside the dead zone. */
      var w = imgW + MOUNT_BRIGHTNESS * (1 - imgW);
      out[0] = r * w; out[1] = g * w; out[2] = b * w;
      return out;
    }

    var heroBox = null;
    function measureGeometry() {
      var r = hero.getBoundingClientRect();
      heroBox = { top: r.top + window.scrollY, bottom: r.bottom + window.scrollY };
    }

    var state = { theme: null, alpha: null, over: null };
    function apply(over, res) {
      if (state.over === over && state.theme === res.theme && state.alpha === res.alpha) return;
      state.over = over; state.theme = res.theme; state.alpha = res.alpha;
      header.classList.toggle('is-over-media', over);
      /* a theme class is always present, including past the hero — leaving both
         off would re-match the pre-JS safe state and put cream on cream */
      header.classList.toggle('on-dark', res.theme === 'on-dark');
      header.classList.toggle('on-light', res.theme === 'on-light');
      header.style.setProperty('--hdr-scrim', over ? String(res.alpha) : '0');
    }

    function resolve() {
      if (!heroBox) measureGeometry();
      var hr = header.getBoundingClientRect();
      /* keyed on the bar's BOTTOM, not its top: while only part of the bar is
         still over the hero, the rest sits on the cream page ground, and cream
         text there is the very bug this replaces */
      var over = hr.bottom + window.scrollY < heroBox.bottom;

      if (!over) {
        /* past the hero the bar is solid ground — nothing to sample */
        apply(false, { theme: 'on-light', alpha: 0 });
        return;
      }
      var box = paintedBox(heroImg);
      var zones = [];
      for (var z = 0; z < ZONES; z++) {
        zones.push(zoneColour(z, hr.top, hr.bottom, box, hr.left, hr.width));
      }
      var res = solve(zones);
      /* hysteresis: hold the incumbent unless the solve genuinely moved, so the
         bar never flaps at a boundary */
      if (state.over === true && state.theme === res.theme &&
          Math.abs(res.alpha - state.alpha) < 0.08) return;
      apply(true, res);
    }

    function whenReady(img, cb) {
      if (img.complete && img.naturalWidth) { cb(); return; }
      if (img.decode) { img.decode().then(cb, cb); return; }
      img.addEventListener('load', cb, { once: true });
      img.addEventListener('error', cb, { once: true });
    }

    whenReady(heroImg, function () {
      if (heroImg.naturalWidth) profile = buildProfile(heroImg);
      measureGeometry();
      resolve();
    });

    var pending = false;
    var onScroll = function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; resolve(); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measureGeometry(); resolve(); });
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { measureGeometry(); resolve(); }).observe(hero);
    }
  }

  /* ---- one-shot scroll reveal (the site's own restrained entrance) ---- */
  var targets = document.querySelectorAll('[data-reveal]');
  if (!('IntersectionObserver' in window)) {
    Array.prototype.forEach.call(targets, function (el) { el.classList.add('in-view'); });
  } else {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    Array.prototype.forEach.call(targets, function (el, i) {
      // The stagger restarts on every row. A grid reveals row by row, so the
      // index has to be the position within the row, not within the whole
      // container — otherwise a 24-card grid runs the delay off the end and
      // clamps flat (card-fidelity audit F-01).
      var group = el.parentElement;
      var idx = group ? Array.prototype.indexOf.call(group.children, el) : i;
      var cols = 0;
      if (group) {
        var tracks = getComputedStyle(group).gridTemplateColumns;
        if (tracks && tracks !== 'none') cols = tracks.split(' ').length;
      }
      el.style.setProperty('--i', cols > 1 ? idx % cols : Math.min(idx, 7));
      io.observe(el);
    });
  }

  /* ---- warm the hover image once its card is on screen ----
     The alternate shot sits at opacity 0, so a lazy loader never fetches it and
     the first hover would flash empty. Fetch it as the card comes into view. */
  var hoverImgs = document.querySelectorAll('.pm-secondary');
  if (hoverImgs.length) {
    var warm = function (img) {
      if (img.dataset.warm) return;
      img.dataset.warm = '1';
      img.loading = 'eager';
      if (img.decode) img.decode().catch(function () {});
    };
    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(hoverImgs, warm);
    } else {
      var wio = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { warm(e.target); wio.unobserve(e.target); }
        });
      // Roughly a viewport of lead time (F-06). The crossfade runs at
      // --dur-slow; a fetch that starts on pointerenter cannot land inside it,
      // so the warm has to be well ahead of the card, not just ahead of the fold.
      }, { rootMargin: '600px 0px' });
      Array.prototype.forEach.call(hoverImgs, function (img) { wio.observe(img); });
    }
    // belt and braces: hovering always guarantees a fetch
    Array.prototype.forEach.call(document.querySelectorAll('.product-item'), function (card) {
      card.addEventListener('pointerenter', function () {
        var s = card.querySelector('.pm-secondary');
        if (s) warm(s);
      }, { once: true });
    });
  }

  /* ---- card swipe galleries: keep the dots in step with the scroll ---- */
  /* named so a card whose media is rebuilt after a colour change can be rewired */
  function bindCardGallery(media) {
    if (!media) return;
    var track = media.querySelector('.pm-swipe');
    var dots = media.querySelectorAll('.pm-dot');
    if (!track || dots.length < 2) return;
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var i = Math.round(track.scrollLeft / track.clientWidth);
        for (var d = 0; d < dots.length; d++) dots[d].classList.toggle('is-active', d === i);
      });
    }, { passive: true });
    // a swipe shouldn't be treated as a click through to the product
    var startX = 0;
    track.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    track.addEventListener('click', function (e) {
      if (Math.abs(track.scrollLeft % track.clientWidth) > 4) { e.preventDefault(); }
    });
  }
  Array.prototype.forEach.call(document.querySelectorAll('.product-media'), bindCardGallery);

  /* ---- PDP gallery: thumbnails drive the stage; dots follow on touch ---- */
  var gallery = document.querySelector('[data-gallery]');
  if (gallery) {
    var slides = gallery.querySelector('.pdp-slides');
    var thumbs = gallery.querySelectorAll('.pdp-thumb');
    var gdots = gallery.querySelectorAll('.pdp-dot');

    var select = function (i) {
      if (!slides) return;
      var slide = slides.children[i];
      if (!slide) return;
      // works for both the desktop (transform) and touch (scroll) presentations
      if (getComputedStyle(slides).overflowX === 'auto') {
        slides.scrollTo({ left: slide.offsetLeft - slides.offsetLeft, behavior: 'smooth' });
      } else {
        slides.style.transform = 'translateX(' + -i * 100 + '%)';
        slides.style.transition = 'transform .42s cubic-bezier(.165,.84,.44,1)'; /* motion.surface; curve kept */
      }
      for (var t = 0; t < thumbs.length; t++) thumbs[t].classList.toggle('is-active', t === i);
      for (var d = 0; d < gdots.length; d++) gdots[d].classList.toggle('is-active', d === i);
    };

    Array.prototype.forEach.call(thumbs, function (btn) {
      var go = function () { select(parseInt(btn.getAttribute('data-index'), 10) || 0); };
      btn.addEventListener('click', go);
      btn.addEventListener('mouseenter', go);
    });

    if (slides) {
      var graf = null;
      slides.addEventListener('scroll', function () {
        if (graf) return;
        graf = requestAnimationFrame(function () {
          graf = null;
          var i = Math.round(slides.scrollLeft / slides.clientWidth);
          for (var t = 0; t < thumbs.length; t++) thumbs[t].classList.toggle('is-active', t === i);
          for (var d = 0; d < gdots.length; d++) gdots[d].classList.toggle('is-active', d === i);
        });
      }, { passive: true });
    }

    gallery.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var cur = 0;
      for (var t = 0; t < thumbs.length; t++) if (thumbs[t].classList.contains('is-active')) cur = t;
      select(Math.max(0, Math.min(thumbs.length - 1, cur + (e.key === 'ArrowRight' ? 1 : -1))));
    });
  }

  /* ---- immersive PDP: media strip driven by the underlined thumbs ---- */
  var strip = document.querySelector('[data-strip]');
  if (strip) {
    var sthumbs = document.querySelectorAll('.pdp-strip-thumb');
    var markPane = function (i) {
      for (var k = 0; k < sthumbs.length; k++) sthumbs[k].classList.toggle('is-active', k === i);
    };
    Array.prototype.forEach.call(sthumbs, function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-index'), 10) || 0;
        var pane = strip.children[i];
        if (pane) strip.scrollTo({ left: pane.offsetLeft - strip.offsetLeft, behavior: 'smooth' });
        markPane(i);
      });
    });
    var sraf = null;
    strip.addEventListener('scroll', function () {
      if (sraf) return;
      sraf = requestAnimationFrame(function () {
        sraf = null;
        var w = strip.children[0] ? strip.children[0].offsetWidth : strip.clientWidth;
        markPane(Math.round(strip.scrollLeft / Math.max(1, w)));
      });
    }, { passive: true });
    strip.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var w = strip.children[0] ? strip.children[0].offsetWidth : strip.clientWidth;
      strip.scrollBy({ left: e.key === 'ArrowRight' ? w : -w, behavior: 'smooth' });
    });
  }

  /* ---- "Select a size" row expands the size list ---- */
  var sizeRow = document.querySelector('.pdp-size-row');
  if (sizeRow) {
    var sizeListEl = document.getElementById('pdp-size-list');
    sizeRow.addEventListener('click', function () {
      var open = sizeListEl.hidden;
      sizeListEl.hidden = !open;
      sizeRow.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---- back pill: real history when there is one, else the href ---- */
  var backBtn = document.querySelector('[data-back]');
  if (backBtn) {
    backBtn.addEventListener('click', function (e) {
      if (history.length > 1) { e.preventDefault(); history.back(); }
    });
  }

  /* ---- PDP accordions ---- */
  Array.prototype.forEach.call(document.querySelectorAll('.pdp-panel-head'), function (head) {
    head.addEventListener('click', function () {
      var panel = head.parentElement;
      var open = panel.classList.toggle('is-open');
      head.setAttribute('aria-expanded', String(open));
    });
  });

  /* ---- size selector ---- */
  Array.prototype.forEach.call(document.querySelectorAll('.pdp-sizes'), function (group) {
    group.addEventListener('click', function (e) {
      var btn = e.target.closest('.pdp-size');
      if (!btn) return;
      Array.prototype.forEach.call(group.children, function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
    });
  });

  /* The facet accordion and checkbox-filtering handlers were removed with the
     filter rail — owner ruling 2026-08-03 (D-38, superseding D-28's surface).
     The D-28 entry in DECISIONS.md remains the spec if filtering returns. */

  /* ---- search suggestions ---- */
  var searchInput = document.getElementById('search-suggestion-field');
  var suggestions = document.getElementById('search-suggestions');
  if (searchInput && suggestions) {
    var openSuggest = function (open) {
      suggestions.hidden = !open;
      searchInput.setAttribute('aria-expanded', String(open));
    };
    searchInput.addEventListener('focus', function () { openSuggest(true); });
    searchInput.addEventListener('input', function () { openSuggest(true); });
    document.addEventListener('click', function (e) {
      if (!suggestions.contains(e.target) && e.target !== searchInput) openSuggest(false);
    });
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { openSuggest(false); searchInput.blur(); }
    });
  }

  /* ---- cart tray ---- */
  var cartToggle = document.querySelector('.cart-toggle');
  var tray = document.getElementById('cart-tray');
  var trayOverlay = document.querySelector('.tray-overlay');
  if (cartToggle && tray && trayOverlay) {
    var setTray = function (open) {
      if (open) {
        tray.hidden = false;
        trayOverlay.hidden = false;
        // next frame so the transform transition actually runs
        requestAnimationFrame(function () { tray.classList.add('is-open'); });
      } else {
        tray.classList.remove('is-open');
        trayOverlay.hidden = true;
        setTimeout(function () { tray.hidden = true; }, 400);
      }
      cartToggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      // dialog focus contract: focus enters the tray on open and returns
      // to the toggle on close (the tray carries role="dialog")
      if (open) {
        var closeBtn = tray.querySelector('.tray-close');
        if (closeBtn) closeBtn.focus();
      } else if (tray.contains(document.activeElement)) {
        cartToggle.focus();
      }
    };
    /* SHOPIFY 3/6: the tray shell — its open/close choreography, focus contract
       and scroll lock — is unchanged; only its contents are now real. Publish
       the opener so tiger-shop.js can raise the tray after an add to cart
       without owning any of that choreography. */
    CFG.openCart = function () { setTray(true); };
    CFG.closeCart = function () { setTray(false); };

    cartToggle.addEventListener('click', function () { setTray(tray.hidden); });
    trayOverlay.addEventListener('click', function () { setTray(false); });
    var trayClose = tray.querySelector('.tray-close');
    if (trayClose) trayClose.addEventListener('click', function () { setTray(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !tray.hidden) setTray(false);
    });
  }

  /* ---- live chat is presentational ----
     SHOPIFY 4/6: quick buy left this list — it adds to the cart for real now,
     in tiger-shop.js. Live chat has no provider yet, and the country selector
     is only rendered when the shop publishes more than one market, in which
     case the footer emits a real localisation form instead of this button. */
  Array.prototype.forEach.call(document.querySelectorAll('.livechat-anchor, .country-selector[aria-disabled="true"]'), function (b) {
    b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); });
  });

  /* SHOPIFY 5/6: the static build pointed search and the newsletter at its
     "not built" explainer page. Here both are real — search posts to /search
     and the newsletter to the shop's customer form — so that note is gone and
     nothing intercepts either submit. */
})();

